import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, ExternalLink, FileText } from "lucide-react";
import { sampleFileUrl } from "@/lib/samples";

export const SampleViewer = ({ items, index, onIndex, onClose }) => {
  const item = items[index];
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight" && index < items.length - 1) onIndex(index + 1);
      if (e.key === "ArrowLeft" && index > 0) onIndex(index - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, items.length, onIndex, onClose]);

  if (!item) return null;
  const url = sampleFileUrl(item.id);
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/95 text-white" data-testid="sample-viewer">
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <div className="min-w-0">
          {item.title && <p className="truncate font-display text-base font-bold" data-testid="sample-viewer-title">{item.title}</p>}
          <p className="text-xs text-white/60" data-testid="sample-viewer-position">
            {index + 1} of {items.length} · {item.type.toUpperCase()}
          </p>
          {item.caption && <p className="mt-1 max-w-3xl text-sm text-white/85" data-testid="sample-viewer-caption">{item.caption}</p>}
        </div>
        <div className="flex items-center gap-2">
          <a href={url} target="_blank" rel="noreferrer" data-testid="sample-open-new-tab" className="flex h-9 items-center gap-1 rounded-md bg-white/10 px-3 text-xs font-bold hover:bg-white/20">
            <ExternalLink size={14} /> Open
          </a>
          <button data-testid="sample-viewer-close" onClick={onClose} aria-label="Close" className="flex h-9 w-9 items-center justify-center rounded-md bg-white/10 hover:bg-white/20">
            <X size={18} />
          </button>
        </div>
      </div>
      <div className="relative flex flex-1 items-center justify-center overflow-hidden px-12 pb-4">
        {item.type === "image" && <img src={url} alt={item.title || "Sample"} className="max-h-full max-w-full object-contain" />}
        {item.type === "video" && <video key={item.id} src={url} controls autoPlay playsInline className="max-h-full max-w-full" />}
        {item.type === "pdf" && (
          <div className="flex h-full w-full flex-col items-center">
            <iframe title={item.title} src={url} className="h-full w-full max-w-5xl rounded-md bg-white" />
            <a href={url} target="_blank" rel="noreferrer" className="mt-3 flex items-center gap-1 text-sm font-bold text-white/80 hover:text-white">
              <FileText size={14} /> Open PDF in a new tab
            </a>
          </div>
        )}
        {index > 0 && (
          <button data-testid="sample-prev" onClick={() => onIndex(index - 1)} aria-label="Previous" className="absolute left-2 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 hover:bg-white/25">
            <ChevronLeft size={22} />
          </button>
        )}
        {index < items.length - 1 && (
          <button data-testid="sample-next" onClick={() => onIndex(index + 1)} aria-label="Next" className="absolute right-2 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 hover:bg-white/25">
            <ChevronRight size={22} />
          </button>
        )}
      </div>
    </div>
  );
};

export const SampleTile = ({ item, checked, onCheck, onOpen, testId, hideCheck = false }) => {
  const url = sampleFileUrl(item.id);
  return (
    <div data-testid={testId} className={`group relative overflow-hidden rounded-lg border bg-card transition-all hover:-translate-y-0.5 hover:shadow-md ${checked ? "border-2 border-[#1B66C9]" : "border-border"}`}>
      <button onClick={onOpen} className="block w-full text-left" data-testid={`${testId}-open`}>
        <div className="relative aspect-[4/3] overflow-hidden bg-muted">
          {item.type === "image" && <img src={url} alt={item.title || "Sample"} loading="lazy" className="h-full w-full object-cover" />}
          {item.type === "video" && <video src={`${url}#t=0.5`} muted preload="metadata" playsInline className="h-full w-full object-cover" />}
          {item.type === "pdf" && (
            <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-secondary text-muted-foreground">
              <FileText size={40} />
              <span className="font-display text-sm font-bold uppercase tracking-widest" data-testid={`${testId}-pdf-label`}>Item details</span>
            </div>
          )}
          <span className="absolute left-3 top-3 rounded-full bg-black/60 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white backdrop-blur">
            {item.type}
          </span>
        </div>
        {(item.title || item.caption) && (
          <div className="p-3">
            {item.title && <p className="truncate font-display text-sm font-bold">{item.title}</p>}
            {item.caption && <p className={`line-clamp-2 text-xs text-muted-foreground ${item.title ? "mt-0.5" : ""}`} data-testid={`${testId}-caption`}>{item.caption}</p>}
          </div>
        )}
      </button>
      {!hideCheck && (
        <label className="absolute right-3 top-3 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-white/90 shadow" onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" data-testid={`${testId}-check`} checked={checked} onChange={onCheck} className="h-4 w-4 accent-[#1B66C9]" aria-label={`Select ${item.title || "sample"}`} />
        </label>
      )}
    </div>
  );
};
